'use client';

import * as React from 'react';
import { Plus, Store } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useCloseMobileSidebar } from '@/components/ui/sidebar';

import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useStoreRescueState } from '@/lib/api/cryptoRescue';
import { useRxIntegrationStatus } from '@/lib/api/rx';
import { cn } from '@/lib/utils';

function normalizePath(path: string) {
  return path.replace(/\/$/, '') || '/';
}

function isPathActive(pathname: string, to: string) {
  const p = normalizePath(pathname);
  const t = normalizePath(to);
  return p === t || p.startsWith(`${t}/`);
}

type StoreAlert = 'setup' | 'rescue';

function StoreIconWithAlert({ alert }: { alert?: StoreAlert }) {
  return (
    <span className="crm-sidebar-store-icon-wrap" aria-hidden>
      <Store className="crm-sidebar-store-icon" strokeWidth={1.75} />
      {alert ? (
        <span
          className={cn(
            'crm-sidebar-store-alert-dot',
            alert === 'setup' && 'crm-sidebar-store-alert-dot--setup',
            alert === 'rescue' && 'crm-sidebar-store-alert-dot--rescue',
          )}
        />
      ) : null}
    </span>
  );
}

/** Orders — une pastille fusionnée, fond plat vert / rose */
export function SidebarOrderSignal({
  toBePaid,
  unsynced,
}: {
  toBePaid?: number;
  unsynced?: number;
}) {
  const { t } = useTranslation('navigation');
  const pay = toBePaid ?? 0;
  const sync = unsynced ?? 0;
  if (!pay && !sync) return null;

  const body = (
    <span
      className="crm-sidebar-order-signal"
      aria-label={
        pay && sync
          ? `${pay} ${t('ordersToBePaid')}, ${sync} ${t('ordersUnsynced')}`
          : pay
            ? String(pay)
            : String(sync)
      }
    >
      {pay > 0 ? (
        <span className="crm-sidebar-order-signal__seg crm-sidebar-order-signal__seg--pay">
          {pay > 99 ? '99+' : pay}
        </span>
      ) : null}
      {sync > 0 ? (
        <span className="crm-sidebar-order-signal__seg crm-sidebar-order-signal__seg--sync">
          {sync > 99 ? '99+' : sync}
        </span>
      ) : null}
    </span>
  );

  if (pay && sync) {
    return (
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <span className="ml-auto shrink-0">{body}</span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[200px] text-xs">
          <p>
            <span className="font-medium text-emerald-600 dark:text-emerald-400">{pay}</span>{' '}
            {t('ordersAwaitingPayment')}
          </p>
          <p className="mt-1">
            <span className="font-medium text-rose-600 dark:text-rose-400">{sync}</span>{' '}
            {t('ordersUnsyncedWithShopify')}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return <span className="ml-auto shrink-0">{body}</span>;
}

interface StoreRowProps {
  to: string;
  name: string;
  isSelected: boolean;
  isActive: boolean;
  needsSetup: boolean;
  setupLabel: string;
  rescueActive?: boolean;
}

function StoreRow({
  to,
  name,
  isSelected,
  isActive,
  needsSetup,
  setupLabel,
  rescueActive,
}: StoreRowProps) {
  const { t } = useTranslation('navigation');
  const closeMobileSidebar = useCloseMobileSidebar();
  const alert: StoreAlert | undefined = rescueActive ? 'rescue' : needsSetup ? 'setup' : undefined;

  const row = (
    <Link
      to={to}
      onClick={closeMobileSidebar}
      className={cn(
        'crm-sidebar-store-row',
        isSelected && 'crm-sidebar-store-row--selected',
        isActive && 'crm-sidebar-store-row--active',
      )}
    >
      <StoreIconWithAlert alert={alert} />
      <span className="min-w-0 flex-1 truncate">{name}</span>
    </Link>
  );

  if (!needsSetup && !rescueActive) return row;

  const tooltipText = needsSetup
    ? setupLabel
    : t('storeRescueActive');

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent
        side="right"
        className={cn(
          'max-w-[220px] border text-xs shadow-sm',
          alert === 'setup' &&
            'border-amber-200/90 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-50',
          alert === 'rescue' &&
            'border-rose-200/90 bg-rose-50 text-rose-950 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-50',
        )}
        arrowClassName={cn(
          alert === 'setup' &&
            'bg-amber-50 fill-amber-50 dark:bg-amber-950 dark:fill-amber-950',
          alert === 'rescue' &&
            'bg-rose-50 fill-rose-50 dark:bg-rose-950 dark:fill-rose-950',
        )}
      >
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}

interface StoreSubLinkProps {
  to: string;
  title: string;
}

function SidebarSubNavLink({ to, title }: StoreSubLinkProps) {
  const location = useLocation();
  const closeMobileSidebar = useCloseMobileSidebar();
  const active = isPathActive(location.pathname, to);

  return (
    <Link
      to={to}
      onClick={closeMobileSidebar}
      className={cn('crm-sidebar-sub-link', active && 'crm-sidebar-sub-link--active')}
    >
      <span className="truncate">{title}</span>
    </Link>
  );
}

function useRescueActive(storeId: string, enabled = true) {
  const { data } = useStoreRescueState(storeId, enabled && !!storeId);
  return !!(data?.configured && data?.active);
}

/**
 * Tagada Rx — conditional store sub-nav entry. Hidden unless the Rx
 * add-on is active for the store; the full Rx nav lives inside the Rx
 * surface itself.
 */
function RxSubNavLink({ storeId, to, enabled }: { storeId: string; to: string; enabled: boolean }) {
  const status = useRxIntegrationStatus(storeId, { enabled: enabled && !!storeId });
  if (status.data?.state !== 'active') return null;
  return <SidebarSubNavLink to={to} title={status.data.rxName} />;
}

export interface SidebarStoreEntryProps {
  store: { id: string; name: string; selectedPaymentFlowId?: string | null };
  storeRoutes: {
    STORE_BASE: () => string;
    STORE_GENERAL: () => string;
    STOREFRONT: () => string;
    EMAILS: () => string;
    PAYMENT_FLOWS_LIST: () => string;
    INTEGRATIONS: () => string;
    STORE_EDIT: () => string;
    /** Tagada Rx — optional; only rendered when the Rx add-on is active. */
    RX_OVERVIEW?: () => string;
  };
  isCurrentStore: boolean;
  displayName: string;
  labels: {
    general: string;
    storefront: string;
    emails: string;
    paymentFlows: string;
    integrations: string;
    storeSettings: string;
  };
}

export const SidebarStoreEntry = React.forwardRef<HTMLLIElement, SidebarStoreEntryProps>(
  function SidebarStoreEntry(
    { store, storeRoutes, isCurrentStore, displayName, labels },
    ref,
  ) {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const basePath = normalizePath(storeRoutes.STORE_BASE());
  const path = normalizePath(location.pathname);
  const needsSetup = !store.selectedPaymentFlowId;
  const rescueActive = useRescueActive(store.id, isCurrentStore);

  const isStoreRootActive = isCurrentStore && path === basePath;

  return (
    <SidebarMenuItem ref={ref} className={cn(isCurrentStore && 'crm-sidebar-store-expanded')}>
      <Collapsible open={isCurrentStore}>
        <SidebarMenuButton
          asChild
          isActive={false}
          className="crm-sidebar-store-button p-0 hover:bg-transparent"
        >
          <div className="w-full">
            <StoreRow
              to={storeRoutes.STORE_BASE()}
              name={displayName}
              isSelected={isCurrentStore}
              isActive={isStoreRootActive}
              needsSetup={needsSetup}
              setupLabel={t('storeNoPaymentFlow')}
              rescueActive={rescueActive}
            />
          </div>
        </SidebarMenuButton>

        <CollapsibleContent forceMount className="crm-sidebar-sub-nav-collapsible">
          <div className="crm-sidebar-sub-nav-inner">
            <nav className="crm-sidebar-sub-nav" aria-label={displayName}>
              <SidebarSubNavLink to={storeRoutes.STORE_GENERAL()} title={labels.general} />
              <SidebarSubNavLink to={storeRoutes.STOREFRONT()} title={labels.storefront} />
              <SidebarSubNavLink to={storeRoutes.EMAILS()} title={labels.emails} />
              <SidebarSubNavLink to={storeRoutes.PAYMENT_FLOWS_LIST()} title={labels.paymentFlows} />
              <SidebarSubNavLink to={storeRoutes.INTEGRATIONS()} title={labels.integrations} />
              {storeRoutes.RX_OVERVIEW ? (
                <RxSubNavLink
                  storeId={store.id}
                  to={storeRoutes.RX_OVERVIEW()}
                  enabled={isCurrentStore}
                />
              ) : null}
              <SidebarSubNavLink to={storeRoutes.STORE_EDIT()} title={labels.storeSettings} />
            </nav>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
  },
);

export function SidebarStoreActions({
  viewAllHref,
  createHref,
  viewAllLabel,
  addStoreLabel,
}: {
  viewAllHref: string;
  createHref: string;
  viewAllLabel: string;
  addStoreLabel: string;
}) {
  const location = useLocation();
  const closeMobileSidebar = useCloseMobileSidebar();
  const path = normalizePath(location.pathname);
  const viewAllActive = path.endsWith('/stores');
  const createActive = path.endsWith('/stores/create');

  return (
    <div className="crm-sidebar-stores-actions">
      <Link
        to={viewAllHref}
        onClick={closeMobileSidebar}
        className={cn('crm-sidebar-action-ghost', viewAllActive && 'crm-sidebar-action-ghost--active')}
      >
        {viewAllLabel}
      </Link>
      <Link
        to={createHref}
        onClick={closeMobileSidebar}
        className={cn('crm-sidebar-action-create', createActive && 'crm-sidebar-action-create--active')}
      >
        <Plus className="size-3.5" strokeWidth={2.5} />
        {addStoreLabel}
      </Link>
    </div>
  );
}

export function SidebarStoreListItem({
  to,
  name,
  isActive,
  hasPaymentFlow,
}: {
  to: string;
  name: string;
  isActive: boolean;
  hasPaymentFlow: boolean;
}) {
  const { t } = useTranslation('navigation');

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={false}
        className="crm-sidebar-store-button p-0 hover:bg-transparent"
      >
        <div className="w-full">
          <StoreRow
            to={to}
            name={name}
            isSelected={isActive}
            isActive={isActive}
            needsSetup={!hasPaymentFlow}
            setupLabel={t('storeNoPaymentFlow')}
          />
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
